import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Text, Heading, Button, Input, Select, SortableTableHeader } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermissions } from '../hooks/usePermissions'
import { useUserLogs } from '../hooks/useUserLogs'
import { supabase } from '../lib/supabase'
import { listDepartments } from '../services/wiki'
import { getUserDepartments, grantUserDepartment, revokeUserDepartment, getGroupDepartments, grantGroupDepartment, revokeGroupDepartment } from '../services/wikiAccess'
import type { WikiDepartment } from '../types/wiki'
import { getSignedAvatarUrl } from '../services/avatar'
import { 
  validateUserForm, 
  checkUserExists, 
  handleAuthError, 
  handleProfileError 
} from '../utils/userValidation'
import { 
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  UserIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'

interface UserProfile {
  id: string
  name: string
  email?: string
  phone?: string
  avatar_url?: string | null
  user_group_id: string
  helpdesk_user_group_id?: string | null
  organization_id?: string | null
  is_active: boolean
  created_at: string
  last_login?: string
  user_group?: {
    id: string
    name: string
    display_name: string
  } | null
  organization?: {
    id: string
    name: string
    display_name: string
  } | null
  helpdesk_user_group?: {
    id: string
    name: string
    display_name: string
  } | null
}

interface UserGroup {
  id: string
  name: string
  display_name: string
  description?: string
  is_admin: boolean
  is_active: boolean
}

interface HelpdeskUserGroup {
  id: string
  name: string
  display_name: string
  description?: string
  is_admin: boolean
  is_active: boolean
}

interface Organization {
  id: string
  name: string
  display_name: string
  description?: string
  is_active: boolean
}

interface Module {
  id: string
  name: string
  display_name: string
  description?: string
  route: string
  icon?: string
  is_active: boolean
  order_index?: number
  parent_module_id?: string
}

interface PermissionWithDetails {
  id: string
  module_id: string
  user_group_id: string
  can_show: boolean
  can_insert: boolean
  can_update: boolean
  can_delete: boolean
  can_view_values: boolean
  module?: Module
  user_group?: UserGroup
}

interface UserPermissions {
  id: string
  user_id: string
  module_id: string
  can_show: boolean
  can_insert: boolean
  can_update: boolean
  can_delete: boolean
  can_view_values: boolean
  module?: {
    id: string
    name: string
    display_name: string
  }
}

// Grupos protegidos que não podem ser excluídos
const PROTECTED_GROUPS = ['admin', 'gerente', 'operador', 'visualizador']

// Módulos onde a permissão "ver valores" faz sentido.
// Para outros módulos, a UI não deve exibir o checkbox.
const VIEW_VALUES_ELIGIBLE_MODULES = new Set<string>([
  'portais_dashboard_corretor',
  'configuracoes_notificacoes',
])

const Usuarios: React.FC = () => {
  const { accessibleModules, isAdmin, loading: permissionsLoading } = usePermissions()
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()
  
  // Estados principais
  const [users, setUsers] = useState<UserProfile[]>([])
  const [userGroups, setUserGroups] = useState<UserGroup[]>([])
  const [helpdeskUserGroups, setHelpdeskUserGroups] = useState<HelpdeskUserGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string | null>>({})
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set())
  
  // Estados dos modais
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null)
  
  // Estados de filtros e busca
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active')
  const [groupFilter, setGroupFilter] = useState('all')
  const [helpdeskGroupFilter, setHelpdeskGroupFilter] = useState('all')
  const [organizationFilter, setOrganizationFilter] = useState('all')
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'last_login' | 'organization' | 'group' | 'status'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  
  // Estados dos formulários
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization_id: '',
    user_group_id: '',
    password: '',
    confirmPassword: ''
  })
  
  // Estado de carregamento para criação
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    organization_id: '',
    user_group_id: '',
    helpdesk_user_group_id: '',
    is_active: true
  })
  
  const [userPermissions, setUserPermissions] = useState<UserPermissions[]>([])
  const [tempPermissions, setTempPermissions] = useState<{
    [key: string]: {
      can_show: boolean
      can_insert: boolean
      can_update: boolean
      can_delete: boolean
      can_view_values: boolean
    }
  }>({})

  // Abas do modal de permissões do usuário (Módulos | Wiki)
  const [permissionsModalTab, setPermissionsModalTab] = useState<'modules' | 'wiki'>('modules')
  // Departamentos (Wiki) para o usuário selecionado
  const [allDepartments, setAllDepartments] = useState<WikiDepartment[]>([])
  const [userDeptIds, setUserDeptIds] = useState<Set<string>>(new Set())
  const [loadingUserDepts, setLoadingUserDepts] = useState<boolean>(false)

  // Estados para visualização de grupos e permissões
  const [activeTab, setActiveTab] = useState<'usuarios' | 'gestao-grupos' | 'organizacoes' | 'permissoes'>('usuarios')
  const [groupPermissions, setGroupPermissions] = useState<{[groupId: string]: PermissionWithDetails[]}>({})
  const [allModules, setAllModules] = useState<Module[]>([])
  const [isGroupUsersModalOpen, setIsGroupUsersModalOpen] = useState(false)
  const [selectedGroupForUsers, setSelectedGroupForUsers] = useState<UserGroup | null>(null)
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)
  const [selectedGroupForPermission, setSelectedGroupForPermission] = useState<UserGroup | null>(null)
  const [selectedModuleForPermission, setSelectedModuleForPermission] = useState<Module | null>(null)
  const [editingPermissions, setEditingPermissions] = useState<{
    can_show: boolean
    can_insert: boolean
    can_update: boolean
    can_delete: boolean
    can_view_values: boolean
  }>({can_show: false, can_insert: false, can_update: false, can_delete: false, can_view_values: false})
  
  // Estados para filtros da matriz
  const [matrixGroupFilter, setMatrixGroupFilter] = useState<string>('all')
  const [matrixModuleFilter, setMatrixModuleFilter] = useState<string>('all')
  
  // Estados para gestão de grupos
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false)
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false)
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null)
  const [groupForm, setGroupForm] = useState({
    name: '',
    display_name: '',
    description: '',
    is_admin: false,
    is_active: true
  })
  const [groupLoading, setGroupLoading] = useState<{[key: string]: boolean}>({})
  
  // Estados para gestão de organizações
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [isCreateOrganizationModalOpen, setIsCreateOrganizationModalOpen] = useState(false)
  const [isEditOrganizationModalOpen, setIsEditOrganizationModalOpen] = useState(false)
  const [isDeleteOrganizationModalOpen, setIsDeleteOrganizationModalOpen] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [organizationForm, setOrganizationForm] = useState({
    name: '',
    display_name: '',
    description: '',
    is_active: true
  })
  const [organizationLoading, setOrganizationLoading] = useState<{[key: string]: boolean}>({})
  
  // Estados para expansão das seções
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  
  // Estados para abas dentro de cada grupo
  const [groupActiveTabs, setGroupActiveTabs] = useState<{[groupId: string]: 'permissions' | 'users' | 'wiki'}>({})
  // Departamentos por grupo (Wiki)
  const [groupDeptIdsByGroup, setGroupDeptIdsByGroup] = useState<{[groupId: string]: Set<string>}>({})
  const [loadingGroupDepts, setLoadingGroupDepts] = useState<boolean>(false)
  
  // Funções para controlar expansão
  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }
  
  const expandAllGroups = () => {
    const allGroupIds = new Set(userGroups.map(group => group.id))
    setExpandedGroups(allGroupIds)
  }
  
  const collapseAllGroups = () => {
    setExpandedGroups(new Set())
  }
  
  // Função para controlar abas dos grupos
  const setGroupActiveTab = async (groupId: string, tab: 'permissions' | 'users' | 'wiki') => {
    setGroupActiveTabs(prev => ({
      ...prev,
      [groupId]: tab
    }))
    if (tab === 'wiki') {
      await loadAllDepartments()
      await loadGroupDepartments(groupId)
    }
  }
  
  // Estados para toast notifications
  const [toast, setToast] = useState<{type: 'success' | 'error' | 'warning' | 'info', message: string} | null>(null)
  const [isLoading, setIsLoading] = useState<{[key: string]: boolean}>({})
  
  // Estado para debounce da busca
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  
  // Estado para controlar qual menu de ações está aberto
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null)
  // Navegação horizontal da lista de usuários
  const usersListScrollRef = useRef<HTMLDivElement | null>(null)

  const scrollUsersListBy = (dx: number) => {
    usersListScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }

  const handleUsersListWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      usersListScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  // Função para gerar iniciais do nome
  const getInitials = (text: string): string => {
    if (!text) return 'N'
    const words = text.trim().split(/\s+/)
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
    return text.substring(0, 2).toUpperCase()
  }

  // Função para gerar cor do avatar
  const getAvatarColor = (text: string): string => {
    const colors = [
      'bg-purple-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500',
    ]
    let hash = 0
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  // Carregar dados iniciais
  useEffect(() => {
    loadUsers()
    loadUserGroups()
    loadHelpdeskUserGroups()
    loadOrganizations()
  }, [])

  // Fechar menu de ações ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openActionMenu) {
        const target = event.target as HTMLElement
        if (!target.closest('.action-menu-container')) {
          setOpenActionMenu(null)
        }
      }
    }

    if (openActionMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openActionMenu])

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timeoutMs = toast.type === 'error' ? 12000 : 5000
      const timer = setTimeout(() => {
        setToast(null)
      }, timeoutMs)
      
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Carregar dados quando trocar para aba de permissões
  useEffect(() => {
    if (activeTab === 'permissoes') {
      loadAllModules()
      loadAllGroupPermissions()
    }
  }, [activeTab, userGroups])


  // Inicializar permissões temporárias quando o modal de permissões abrir
  useEffect(() => {
    if (isPermissionsModalOpen && selectedUser && userPermissions.length > 0) {
      // Inicializar permissões temporárias
      const temp: {
        [key: string]: {
          can_show: boolean
          can_insert: boolean
          can_update: boolean
          can_delete: boolean
          can_view_values: boolean
        }
      } = {}
      
      allModules.forEach(module => {
        const userPermission = userPermissions.find(p => p.module_id === module.id)
        temp[module.id] = {
          can_show: userPermission?.can_show || false,
          can_insert: userPermission?.can_insert || false,
          can_update: userPermission?.can_update || false,
          can_delete: userPermission?.can_delete || false,
          // Permissão seletiva (a UI só exibe para módulos elegíveis)
          can_view_values: VIEW_VALUES_ELIGIBLE_MODULES.has(module.name)
            ? (userPermission?.can_view_values || false)
            : false
        }
      })
      
      setTempPermissions(temp)
    }
  }, [isPermissionsModalOpen, selectedUser, userPermissions, allModules])

  // Carregar usuários
  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      // Consulta simples: buscar perfis com relacionamento
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          user_group:user_groups(id, name, display_name),
          organization:organizations(id, name, display_name),
          helpdesk_user_group:helpdesk_user_groups(id, name, display_name)
        `)
        .order('name')

      if (error) throw error

      // Buscar emails dos usuários usando função segura
      if (data && data.length > 0) {
        const userIds = data.map(u => u.id)
        
        try {
          const { data: usersInfo, error: usersError } = await supabase
            .rpc('get_multiple_users_info', { user_ids: userIds })
          
          if (!usersError && usersInfo) {
            const usersMap = new Map<string, { id: string; email: string; name: string }>(
              usersInfo.map((u: any) => [u.id, u])
            )
            
            data.forEach(user => {
              const userInfo = usersMap.get(user.id)
              if (userInfo) {
                user.email = userInfo.email
              }
            })
          }
        } catch (err) {
          // Continuar mesmo com erro
        }
      }

      setUsers(data || [])

      // Carregar URLs assinadas dos avatares
      if (data && data.length > 0) {
        const avatarUrlPromises = data
          .filter(user => user.avatar_url)
          .map(async (user) => {
            const signedUrl = await getSignedAvatarUrl(user.avatar_url)
            return { userId: user.id, url: signedUrl }
          })
        
        const avatarResults = await Promise.all(avatarUrlPromises)
        const avatarUrlMap: Record<string, string | null> = {}
        avatarResults.forEach(({ userId, url }) => {
          avatarUrlMap[userId] = url
        })
        setAvatarUrls(avatarUrlMap)
      }
    } catch (err) {
      console.error('Erro ao carregar usuários:', err)
      setError('Erro ao carregar usuários')
    } finally {
      setLoading(false)
    }
  }

  // Carregar grupos de usuários
  const loadUserGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .order('display_name')

      if (error) throw error

      setUserGroups(data || [])
    } catch (err) {
      console.error('Erro ao carregar grupos:', err)
    }
  }

  // Carregar grupos do portal de atendimento
  const loadHelpdeskUserGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('helpdesk_user_groups')
        .select('*')
        .order('display_name')

      if (error) throw error

      setHelpdeskUserGroups(data || [])
    } catch (err) {
      console.error('Erro ao carregar grupos do portal de atendimento:', err)
    }
  }

  // Carregar organizações
  const loadOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('display_name')

      if (error) throw error

      setOrganizations(data || [])
    } catch (err) {
      console.error('Erro ao carregar organizações:', err)
    }
  }

  // Reenviar e-mail de confirmação de cadastro
  const handleResendConfirmation = async (user: UserProfile) => {
    const loadingKey = `resend-${user.id}`

    if (!user.email) {
      setToast({ type: 'error', message: 'Usuário sem e-mail cadastrado.' })
      return
    }

    setIsLoading(prev => ({ ...prev, [loadingKey]: true }))
    try {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email
      })

      if (error) throw error
      setToast({ type: 'success', message: `E-mail de confirmação reenviado para ${user.email}.` })
    } catch (e) {
      const msg = (e as any)?.message || (typeof e === 'string' ? e : JSON.stringify(e)) || 'Erro ao reenviar e-mail de confirmação.'
      setToast({ type: 'error', message: String(msg) })
    } finally {
      setIsLoading(prev => ({ ...prev, [loadingKey]: false }))
    }
  }

  // Carregar todos os módulos
  const loadAllModules = async () => {
    try {
      // Preferir RPC sem parâmetros criada para listar todos os módulos ativos
      let data: any[] | null = null
      try {
        const { data: { user } } = await supabase.auth.getUser()
        const { data: rpcData, error: rpcError } = user
          ? await supabase.rpc('get_user_accessible_modules', { user_uuid: user.id })
          : { data: null, error: null }
        if (rpcError) {
          console.warn('RPC get_user_accessible_modules() falhou, usando fallback direto na tabela modules:', rpcError)
        } else {
          data = rpcData as any[]
        }
      } catch (rpcErr) {
        console.warn('Erro ao chamar RPC get_user_accessible_modules():', rpcErr)
      }

      if (!data) {
        const { data: tableData, error: tableError } = await supabase
          .from('modules')
          .select('*')
          .eq('is_active', true)
          .order('order_index')
        if (tableError) throw tableError
        data = tableData as any[]
      }

      if (data) {
        setAllModules(([...data] as any[]).sort((a: any, b: any) => (a.order_index ?? 1e9) - (b.order_index ?? 1e9)))
      }
    } catch (err) {
      console.error('Erro ao carregar módulos:', err)
    }
  }

  // Carregar permissões de todos os grupos
  const loadAllGroupPermissions = async () => {
    try {
      const permissions: {[key: string]: PermissionWithDetails[]} = {}
      
      for (const group of userGroups) {
        const { data } = await supabase
          .from('permissions')
          .select('*, module:modules(*), user_group:user_groups(*)')
          .eq('user_group_id', group.id)
        
        permissions[group.id] = data || []
      }
      
      setGroupPermissions(permissions)
    } catch (err) {
      console.error('Erro ao carregar permissões dos grupos:', err)
    }
  }


  // Carregar departamentos da Wiki (todos)
  const loadAllDepartments = async () => {
    try {
      const depts = await listDepartments()
      setAllDepartments(depts)
    } catch (err) {
      console.error('Erro ao carregar departamentos da Wiki:', err)
    }
  }

  // Carregar departamentos vinculados do usuário
  const loadUserDepartments = async (userId: string) => {
    try {
      setLoadingUserDepts(true)
      const depts = await getUserDepartments(userId)
      setUserDeptIds(new Set(depts.map(d => d.id)))
    } catch (err) {
      console.error('Erro ao carregar departamentos do usuário:', err)
    } finally {
      setLoadingUserDepts(false)
    }
  }

  // Alternar vínculo do usuário com departamento
  const toggleUserDepartment = async (deptId: string, checked: boolean) => {
    if (!selectedUser) return
    try {
      setLoadingUserDepts(true)
      if (checked) {
        await grantUserDepartment(selectedUser.id, deptId)
      } else {
        await revokeUserDepartment(selectedUser.id, deptId)
      }
      await loadUserDepartments(selectedUser.id)
    } catch (err) {
      console.error('Erro ao atualizar departamento do usuário:', err)
    } finally {
      setLoadingUserDepts(false)
    }
  }

  // Carregar departamentos vinculados de um grupo
  const loadGroupDepartments = async (groupId: string) => {
    try {
      setLoadingGroupDepts(true)
      const depts = await getGroupDepartments(groupId)
      setGroupDeptIdsByGroup(prev => ({
        ...prev,
        [groupId]: new Set(depts.map(d => d.id))
      }))
    } catch (err) {
      console.error('Erro ao carregar departamentos do grupo:', err)
    } finally {
      setLoadingGroupDepts(false)
    }
  }

  // Alternar vínculo do grupo com departamento
  const toggleGroupDepartment = async (groupId: string, deptId: string, checked: boolean) => {
    try {
      setLoadingGroupDepts(true)
      if (checked) {
        await grantGroupDepartment(groupId, deptId)
      } else {
        await revokeGroupDepartment(groupId, deptId)
      }
      await loadGroupDepartments(groupId)
    } catch (err) {
      console.error('Erro ao atualizar departamento do grupo:', err)
    } finally {
      setLoadingGroupDepts(false)
    }
  }


  // Calcular nível de acesso para cor da célula
  const getAccessLevel = (permissions: {can_show: boolean, can_insert: boolean, can_update: boolean, can_delete: boolean}) => {
    const { can_show, can_insert, can_update, can_delete } = permissions
    const count = [can_show, can_insert, can_update, can_delete].filter(Boolean).length
    
    if (count === 0) return 'none'
    if (count === 1 && can_show) return 'view'
    if (count === 4) return 'full'
    return 'partial'
  }

  // Construir árvore pai → filhos a partir de uma lista plana de módulos
  const buildModuleTree = (modules: Module[]) => {
    const byId = new Map<string, Module & { children: Module[] }>(
      modules.map(m => [m.id, { ...m, children: [] }])
    )
    const roots: (Module & { children: Module[] })[] = []

    byId.forEach((m) => {
      if (m.parent_module_id && byId.has(m.parent_module_id)) {
        byId.get(m.parent_module_id)!.children.push(m)
      } else {
        roots.push(m)
      }
    })

    const sortFn = (a: Module, b: Module) => (a.order_index ?? 1e9) - (b.order_index ?? 1e9)
    byId.forEach((n) => (n as any).children.sort(sortFn))
    roots.sort(sortFn)

    return roots
  }

  // Abrir modal de permissões
  const openPermissionModal = (groupId: string, moduleId: string) => {
    const group = userGroups.find(g => g.id === groupId)
    const module = allModules.find(m => m.id === moduleId)
    const permissions = groupPermissions[groupId]?.find(p => p.module_id === moduleId)
    
    if (group && module) {
      setSelectedGroupForPermission(group)
      setSelectedModuleForPermission(module)
      setEditingPermissions({
        can_show: permissions?.can_show || false,
        can_insert: permissions?.can_insert || false,
        can_update: permissions?.can_update || false,
        can_delete: permissions?.can_delete || false,
        can_view_values: VIEW_VALUES_ELIGIBLE_MODULES.has(module.name)
          ? (permissions?.can_view_values || false)
          : false
      })
      setIsPermissionModalOpen(true)
    }
  }

  // Marcar todas as permissões
  const handleSelectAllPermissions = () => {
    const eligibleForValues = selectedModuleForPermission
      ? VIEW_VALUES_ELIGIBLE_MODULES.has(selectedModuleForPermission.name)
      : false
    setEditingPermissions({
      can_show: true,
      can_insert: true,
      can_update: true,
      can_delete: true,
      can_view_values: eligibleForValues
    })
  }

  // Desmarcar todas as permissões
  const handleDeselectAllPermissions = () => {
    setEditingPermissions({
      can_show: false,
      can_insert: false,
      can_update: false,
      can_delete: false,
      can_view_values: false
    })
  }

  // Salvar permissões do modal
  const savePermissionModal = async () => {
    if (!selectedGroupForPermission || !selectedModuleForPermission) return
    
    try {
      await supabase
        .from('permissions')
        .upsert([
          {
            user_group_id: selectedGroupForPermission.id,
            module_id: selectedModuleForPermission.id,
            ...editingPermissions
          }
        ], { onConflict: 'user_group_id, module_id' })
        .select()
      
      // Registrar log de atualização de permissões do grupo
      await logUpdate(
        'permission',
        selectedGroupForPermission.id,
        `Atualizou permissões do grupo ${selectedGroupForPermission.display_name} para o módulo ${selectedModuleForPermission.display_name}`,
        undefined,
        undefined,
        {
          group_id: selectedGroupForPermission.id,
          module_id: selectedModuleForPermission.id,
          permissions: editingPermissions,
        }
      )
      
      setIsPermissionModalOpen(false)
      setSelectedGroupForPermission(null)
      setSelectedModuleForPermission(null)
      await loadAllGroupPermissions()
    } catch (err) {
      console.error('Erro ao salvar permissões:', err)
    }
  }

  // Funções para gestão de grupos
  const createGroup = async () => {
    try {
      setGroupLoading({ create: true })
      
      const { data, error } = await supabase
        .from('user_groups')
        .insert({
          name: (groupForm.name || '').toLowerCase().replace(/\s+/g, '_'),
          display_name: groupForm.display_name,
          description: groupForm.description,
          is_admin: groupForm.is_admin,
          is_active: groupForm.is_active
        })
        .select()

      if (error) throw error

      // Recarregar grupos
      await loadUserGroups()
      
      // Registrar log de criação de grupo
      if (data && data[0]) {
        await logCreate('user_group', data[0].id, `Criou grupo ${groupForm.display_name}`, {
          name: data[0].name,
          display_name: groupForm.display_name,
          description: groupForm.description,
          is_admin: groupForm.is_admin,
          is_active: groupForm.is_active,
        })
      }
      
      // Limpar formulário e fechar modal
      setGroupForm({
        name: '',
        display_name: '',
        description: '',
        is_admin: false,
        is_active: true
      })
      setIsCreateGroupModalOpen(false)
      
      setToast({ type: 'success', message: 'Grupo criado com sucesso!' })
    } catch (err) {
      console.error('Erro ao criar grupo:', err)
      setToast({ type: 'error', message: 'Erro ao criar grupo' })
    } finally {
      setGroupLoading({ create: false })
    }
  }

  const updateGroup = async () => {
    if (!selectedGroup) return
    
    try {
      setGroupLoading({ edit: true })
      
      const { error } = await supabase
        .from('user_groups')
        .update({
          display_name: groupForm.display_name,
          description: groupForm.description,
          is_admin: groupForm.is_admin,
          is_active: groupForm.is_active
        })
        .eq('id', selectedGroup.id)

      if (error) throw error

      // Capturar valores antigos para o log
      const oldValues = {
        display_name: selectedGroup.display_name,
        description: selectedGroup.description || '',
        is_admin: selectedGroup.is_admin,
        is_active: selectedGroup.is_active,
      }

      // Registrar log de atualização de grupo
      await logUpdate(
        'user_group',
        selectedGroup.id,
        `Editou grupo ${groupForm.display_name}`,
        oldValues,
        {
          display_name: groupForm.display_name,
          description: groupForm.description,
          is_admin: groupForm.is_admin,
          is_active: groupForm.is_active,
        }
      )

      // Recarregar grupos
      await loadUserGroups()
      
      // Fechar modal
      setIsEditGroupModalOpen(false)
      setSelectedGroup(null)
      
      setToast({ type: 'success', message: 'Grupo atualizado com sucesso!' })
    } catch (err) {
      console.error('Erro ao atualizar grupo:', err)
      setToast({ type: 'error', message: 'Erro ao atualizar grupo' })
    } finally {
      setGroupLoading({ edit: false })
    }
  }

  const deleteGroup = async () => {
    if (!selectedGroup) return
    
    try {
      setGroupLoading({ delete: true })
      
      // Verificar se tem usuários
      const { data: users } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_group_id', selectedGroup.id)
      
      if (users && users.length > 0) {
        setToast({ 
          type: 'error', 
          message: `Não é possível excluir o grupo. Existem ${users.length} usuário(s) vinculado(s).` 
        })
        setIsDeleteGroupModalOpen(false)
        setSelectedGroup(null)
        return
      }
      
      const { error } = await supabase
        .from('user_groups')
        .delete()
        .eq('id', selectedGroup.id)

      if (error) throw error

      // Registrar log de exclusão de grupo
      await logDelete('user_group', selectedGroup.id, `Excluiu grupo ${selectedGroup.display_name}`)

      // Recarregar grupos
      await loadUserGroups()
      
      // Fechar modal
      setIsDeleteGroupModalOpen(false)
      setSelectedGroup(null)
      
      setToast({ type: 'success', message: 'Grupo excluído com sucesso!' })
    } catch (err) {
      console.error('Erro ao excluir grupo:', err)
      setToast({ type: 'error', message: 'Erro ao excluir grupo' })
    } finally {
      setGroupLoading({ delete: false })
    }
  }

  // Abrir modal de criação
  const openCreateGroupModal = () => {
    setGroupForm({
      name: '',
      display_name: '',
      description: '',
      is_admin: false,
      is_active: true
    })
    setIsCreateGroupModalOpen(true)
  }

  // Abrir modal de edição
  const openEditGroupModal = (group: UserGroup) => {
    setSelectedGroup(group)
    setGroupForm({
      name: group.name,
      display_name: group.display_name,
      description: group.description || '',
      is_admin: group.is_admin,
      is_active: group.is_active
    })
    setIsEditGroupModalOpen(true)
  }

  // Abrir modal de exclusão
  const openDeleteGroupModal = (group: UserGroup) => {
    setSelectedGroup(group)
    setIsDeleteGroupModalOpen(true)
  }

  // Verificar se grupo é protegido
  const isProtectedGroup = (groupName: string) => PROTECTED_GROUPS.includes(groupName)

  // Funções para gestão de organizações
  const createOrganization = async () => {
    try {
      setOrganizationLoading({ create: true })
      
      const { data, error } = await supabase
        .from('organizations')
        .insert({
          name: (organizationForm.name || '').toLowerCase().replace(/\s+/g, '_'),
          display_name: organizationForm.display_name,
          description: organizationForm.description,
          is_active: organizationForm.is_active
        })
        .select()

      if (error) throw error

      // Recarregar organizações
      await loadOrganizations()
      
      // Registrar log de criação de organização
      if (data && data[0]) {
        await logCreate('organization', data[0].id, `Criou organização ${organizationForm.display_name}`, {
          name: data[0].name,
          display_name: organizationForm.display_name,
          description: organizationForm.description,
          is_active: organizationForm.is_active,
        })
      }
      
      // Limpar formulário e fechar modal
      setOrganizationForm({
        name: '',
        display_name: '',
        description: '',
        is_active: true
      })
      setIsCreateOrganizationModalOpen(false)
      
      setToast({ type: 'success', message: 'Organização criada com sucesso!' })
    } catch (err) {
      console.error('Erro ao criar organização:', err)
      setToast({ type: 'error', message: 'Erro ao criar organização' })
    } finally {
      setOrganizationLoading({ create: false })
    }
  }

  const updateOrganization = async () => {
    if (!selectedOrganization) return
    
    try {
      setOrganizationLoading({ edit: true })
      
      const { error } = await supabase
        .from('organizations')
        .update({
          display_name: organizationForm.display_name,
          description: organizationForm.description,
          is_active: organizationForm.is_active
        })
        .eq('id', selectedOrganization.id)

      if (error) throw error

      // Capturar valores antigos para o log
      const oldValues = {
        display_name: selectedOrganization.display_name,
        description: selectedOrganization.description || '',
        is_active: selectedOrganization.is_active,
      }

      // Registrar log de atualização de organização
      await logUpdate(
        'organization',
        selectedOrganization.id,
        `Editou organização ${organizationForm.display_name}`,
        oldValues,
        {
          display_name: organizationForm.display_name,
          description: organizationForm.description,
          is_active: organizationForm.is_active,
        }
      )

      // Recarregar organizações
      await loadOrganizations()
      
      // Fechar modal
      setIsEditOrganizationModalOpen(false)
      setSelectedOrganization(null)
      
      setToast({ type: 'success', message: 'Organização atualizada com sucesso!' })
    } catch (err) {
      console.error('Erro ao atualizar organização:', err)
      setToast({ type: 'error', message: 'Erro ao atualizar organização' })
    } finally {
      setOrganizationLoading({ edit: false })
    }
  }

  const deleteOrganization = async () => {
    if (!selectedOrganization) return
    
    try {
      setOrganizationLoading({ delete: true })
      
      // Verificar se tem usuários
      const { data: users } = await supabase
        .from('profiles')
        .select('id')
        .eq('organization_id', selectedOrganization.id)
      
      if (users && users.length > 0) {
        setToast({ 
          type: 'error', 
          message: `Não é possível excluir a organização. Existem ${users.length} usuário(s) vinculado(s).` 
        })
        setIsDeleteOrganizationModalOpen(false)
        setSelectedOrganization(null)
        return
      }
      
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', selectedOrganization.id)

      if (error) throw error

      // Registrar log de exclusão de organização
      await logDelete('organization', selectedOrganization.id, `Excluiu organização ${selectedOrganization.display_name}`)

      // Recarregar organizações
      await loadOrganizations()
      
      // Fechar modal
      setIsDeleteOrganizationModalOpen(false)
      setSelectedOrganization(null)
      
      setToast({ type: 'success', message: 'Organização excluída com sucesso!' })
    } catch (err) {
      console.error('Erro ao excluir organização:', err)
      setToast({ type: 'error', message: 'Erro ao excluir organização' })
    } finally {
      setOrganizationLoading({ delete: false })
    }
  }

  // Abrir modal de criação
  const openCreateOrganizationModal = () => {
    setOrganizationForm({
      name: '',
      display_name: '',
      description: '',
      is_active: true
    })
    setIsCreateOrganizationModalOpen(true)
  }

  // Abrir modal de edição
  const openEditOrganizationModal = (organization: Organization) => {
    setSelectedOrganization(organization)
    setOrganizationForm({
      name: organization.name,
      display_name: organization.display_name,
      description: organization.description || '',
      is_active: organization.is_active
    })
    setIsEditOrganizationModalOpen(true)
  }

  // Abrir modal de exclusão
  const openDeleteOrganizationModal = (organization: Organization) => {
    setSelectedOrganization(organization)
    setIsDeleteOrganizationModalOpen(true)
  }

  // Obter usuários por organização
  const getUsersByOrganization = (organizationId: string) => users.filter(u => u.organization_id === organizationId)

  // Abrir modal de usuários do grupo
  const openGroupUsersModal = (group: UserGroup) => {
    setSelectedGroupForUsers(group)
    setIsGroupUsersModalOpen(true)
  }

  // Obter usuários por grupo
  const getUsersByGroup = (groupId: string) => users.filter(u => u.user_group_id === groupId)

  // Carregar permissões do usuário
  const loadUserPermissions = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_permissions')
        .select(`
          *,
          module:modules(*)
        `)
        .eq('user_id', userId)

      if (error) throw error

      setUserPermissions(data || [])
    } catch (err) {
      console.error('Erro ao carregar permissões:', err)
    }
  }

  // Abrir modal de criação
  const handleOpenCreateModal = () => {
    setCreateForm({
      name: '',
      email: '',
      phone: '',
      organization_id: '',
      user_group_id: '',
      password: '',
      confirmPassword: ''
    })
    setError(null)
    setIsCreateModalOpen(true)
  }

  // Criar usuário
  const handleCreateUser = async () => {
    try {
      setError(null)
      setIsCreatingUser(true)
      
      // Validação completa do formulário usando função utilitária
      const formValidation = validateUserForm(createForm)
      if (!formValidation.isValid) {
        setError(formValidation.error!)
        return
      }

      // Verificar se o usuário já existe antes de tentar criar
      const userCheck = await checkUserExists(createForm.email.trim())
      if (userCheck.exists) {
        setError('Erro ao criar usuário: Este email já está cadastrado. Use outro email.')
        return
      }

      // Criar usuário no auth usando o email fornecido pelo usuário
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: createForm.email.trim(),
        password: createForm.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      })

      if (authError) {
        throw new Error(handleAuthError(authError))
      }

      if (authData.user) {
        
        // Criar/atualizar perfil (idempotente)
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: authData.user.id,
            name: createForm.name.trim(),
            phone: createForm.phone.trim(),
            organization_id: createForm.organization_id || null,
            user_group_id: createForm.user_group_id,
            is_active: true
          }, { onConflict: 'id' })

        if (profileError) {
          throw new Error(handleProfileError(profileError))
        }

        setToast({ type: 'success', message: `Usuário criado. Enviamos um e-mail de confirmação para ${createForm.email.trim()}.` })
        
        // Registrar log de criação
        await logCreate('user', authData.user.id, `Criou usuário ${createForm.name.trim()}`, {
          email: createForm.email.trim(),
          phone: createForm.phone.trim(),
          organization_id: createForm.organization_id,
          user_group_id: createForm.user_group_id,
        })
        
        // Recarregar usuários
        await loadUsers()
        
        // Limpar formulário e fechar modal
        setCreateForm({
          name: '',
          email: '',
          phone: '',
          organization_id: '',
          user_group_id: '',
          password: '',
          confirmPassword: ''
        })
        setIsCreateModalOpen(false)
      }
    } catch (err: any) {
      console.error('Erro ao criar usuário:', err)
      const msg = err?.message || 'Erro ao criar usuário'
      setError(msg)
      setToast({ type: 'error', message: msg })
    } finally {
      setIsCreatingUser(false)
    }
  }

  // Editar usuário
  const handleEditUser = async () => {
    try {
      if (!selectedUser) return

      // Capturar valores antigos para o log
      const oldValues = {
        name: selectedUser.name,
        phone: selectedUser.phone || '',
        organization_id: selectedUser.organization_id || null,
        user_group_id: selectedUser.user_group_id,
        helpdesk_user_group_id: selectedUser.helpdesk_user_group_id || null,
        is_active: selectedUser.is_active,
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          name: editForm.name,
          phone: editForm.phone,
          organization_id: editForm.organization_id || null,
          user_group_id: editForm.user_group_id,
          helpdesk_user_group_id: editForm.helpdesk_user_group_id || null,
          is_active: editForm.is_active
        })
        .eq('id', selectedUser.id)

      if (error) throw error

      // Registrar log de atualização
      await logUpdate(
        'user',
        selectedUser.id,
        `Editou usuário ${editForm.name}`,
        oldValues,
        {
          name: editForm.name,
          phone: editForm.phone,
          organization_id: editForm.organization_id,
          user_group_id: editForm.user_group_id,
          helpdesk_user_group_id: editForm.helpdesk_user_group_id || null,
          is_active: editForm.is_active,
        }
      )

      // Recarregar usuários
      await loadUsers()
      
      // Fechar modal
      setIsEditModalOpen(false)
      setSelectedUser(null)
    } catch (err) {
      console.error('Erro ao editar usuário:', err)
      setError('Erro ao editar usuário')
    }
  }

  // Excluir usuário
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja desativar este usuário?')) return

    try {
      // Buscar dados do usuário antes de desativar para o log
      const user = users.find(u => u.id === userId)
      const userName = user?.name || 'Usuário desconhecido'

      // Desativar usuário (não excluir fisicamente)
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: false })
        .eq('id', userId)

      if (error) throw error

      // Registrar log de desativação
      await logDelete('user', userId, `Desativou usuário ${userName}`)

      setToast({ type: 'success', message: 'Usuário desativado com sucesso!' })
      // Recarregar usuários
      await loadUsers()
    } catch (err) {
      console.error('Erro ao desativar usuário:', err)
      setToast({ type: 'error', message: 'Erro ao desativar usuário' })
      setError('Erro ao desativar usuário')
    }
  }

  // Abrir modal de edição
  const openEditModal = (user: UserProfile) => {
    setSelectedUser(user)
    setEditForm({
      name: user.name,
      phone: user.phone || '',
      organization_id: user.organization_id || '',
      user_group_id: user.user_group_id,
      helpdesk_user_group_id: user.helpdesk_user_group_id || '',
      is_active: user.is_active
    })
    setIsEditModalOpen(true)
  }

  // Abrir modal de visualização
  const openViewModal = async (user: UserProfile) => {
    setSelectedUser(user)
    await loadUserPermissions(user.id)
    setIsViewModalOpen(true)
    
    // Registrar log de visualização
    await logView('user', user.id, `Visualizou detalhes do usuário ${user.name}`)
  }

  // Abrir modal de permissões
  const openPermissionsModal = async (user: UserProfile) => {
    setSelectedUser(user)
    await loadUserPermissions(user.id)
    await loadAllModules()
    await loadAllDepartments()
    await loadUserDepartments(user.id)
    setPermissionsModalTab('modules')
    setIsPermissionsModalOpen(true)
  }



  // Atualizar permissão temporária
  const updateTempPermission = (
    moduleId: string,
    permissionType: 'can_show' | 'can_insert' | 'can_update' | 'can_delete' | 'can_view_values',
    value: boolean
  ) => {
    setTempPermissions(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        [permissionType]: value
      }
    }))
  }

  // Marcar todas as permissões
  const selectAllPermissions = (
    permissionType: 'can_show' | 'can_insert' | 'can_update' | 'can_delete' | 'can_view_values',
    value: boolean
  ) => {
    const newTempPermissions = { ...tempPermissions }
    
    allModules.forEach(module => {
      if (newTempPermissions[module.id]) {
        newTempPermissions[module.id][permissionType] = value
      }
    })
    
    setTempPermissions(newTempPermissions)
  }

  // Verificar se todas as permissões de um tipo estão marcadas
  const areAllPermissionsSelected = (
    permissionType: 'can_show' | 'can_insert' | 'can_update' | 'can_delete' | 'can_view_values'
  ) => {
    return allModules.every(module => 
      tempPermissions[module.id]?.[permissionType] === true
    )
  }

  // Verificar se pelo menos uma permissão de um tipo está marcada
  const areSomePermissionsSelected = (
    permissionType: 'can_show' | 'can_insert' | 'can_update' | 'can_delete' | 'can_view_values'
  ) => {
    return allModules.some(module => 
      tempPermissions[module.id]?.[permissionType] === true
    )
  }

  // Salvar permissões
  const savePermissions = async () => {
    try {
      if (!selectedUser) return

      // Preparar dados para salvar
      const permissionsToSave = allModules.map(module => ({
        user_id: selectedUser.id,
        module_id: module.id,
        ...tempPermissions[module.id]
      }))

      // Salvar no banco de dados
      const { error } = await supabase
        .from('user_permissions')
        .upsert(permissionsToSave, { onConflict: 'user_id,module_id' })

      if (error) throw error

      // Registrar log de atualização de permissões
      const changedPermissions = Object.keys(tempPermissions).filter(
        moduleId => tempPermissions[moduleId]
      )
      await logUpdate(
        'user_permissions',
        selectedUser.id,
        `Atualizou permissões do usuário ${selectedUser.name}`,
        undefined,
        undefined,
        {
          modules_updated: changedPermissions.length,
          permissions: tempPermissions,
        }
      )

      // Recarregar permissões
      await loadUserPermissions(selectedUser.id)
      
      // Fechar modal
      setIsPermissionsModalOpen(false)
    } catch (err) {
      setError('Erro ao salvar permissões')
    }
  }

  // Filtrar e ordenar usuários
  const filteredAndSortedUsers = users
    .filter(user => {
      const searchTerm = (debouncedSearchTerm || '').toLowerCase()
      const matchesSearch = user.name?.toLowerCase().includes(searchTerm) || 
                           user.email?.toLowerCase().includes(searchTerm) || false
      
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'active' && user.is_active) ||
                           (statusFilter === 'inactive' && !user.is_active)
      
      const matchesGroup = groupFilter === 'all' || user.user_group_id === groupFilter
      const matchesHelpdeskGroup = helpdeskGroupFilter === 'all'
        ? true
        : helpdeskGroupFilter === 'none'
          ? !user.helpdesk_user_group_id
          : user.helpdesk_user_group_id === helpdeskGroupFilter
      
      const matchesOrganization = organizationFilter === 'all' 
        ? true 
        : organizationFilter === 'none' 
          ? !user.organization_id 
          : user.organization_id === organizationFilter
      
      return matchesSearch && matchesStatus && matchesGroup && matchesHelpdeskGroup && matchesOrganization
    })
    .sort((a, b) => {
      let aValue: any
      let bValue: any

      if (sortBy === 'name') {
        aValue = (a.name || '').toLowerCase()
        bValue = (b.name || '').toLowerCase()
      } else if (sortBy === 'created_at') {
        aValue = new Date(a.created_at).getTime()
        bValue = new Date(b.created_at).getTime()
      } else if (sortBy === 'last_login') {
        aValue = a.last_login ? new Date(a.last_login).getTime() : 0
        bValue = b.last_login ? new Date(b.last_login).getTime() : 0
      } else if (sortBy === 'organization') {
        aValue = (a.organization?.display_name || a.organization?.name || '').toLowerCase()
        bValue = (b.organization?.display_name || b.organization?.name || '').toLowerCase()
      } else if (sortBy === 'group') {
        aValue = (a.user_group?.display_name || a.user_group?.name || '').toLowerCase()
        bValue = (b.user_group?.display_name || b.user_group?.name || '').toLowerCase()
      } else if (sortBy === 'status') {
        // Ativos primeiro quando asc, inativos primeiro quando desc
        aValue = a.is_active ? 1 : 0
        bValue = b.is_active ? 1 : 0
      } else {
        aValue = ''
        bValue = ''
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, statusFilter, groupFilter, helpdeskGroupFilter, organizationFilter])

  // Calcular dados da paginação
  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const usersPaginados = filteredAndSortedUsers.slice(startIndex, endIndex)

  // Alternar ordem de classificação
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field as typeof sortBy)
      setSortOrder('asc')
    }
  }

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  return (
    <div className="flex min-h-screen bg-gray-100 dark:bg-gray-900 overflow-x-hidden">
      <Sidebar />
      
      <div className="flex-1 min-w-0 flex flex-col overflow-x-hidden">
        <main className="flex-1 min-w-0 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard 
            moduleName="configuracoes_usuarios" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
          </div>
        </div>
            }
          >
            <div className="py-6 px-4 sm:px-6 lg:px-8 min-w-0">
        {/* Header */}
              <div className="mb-8">
          <div className="flex items-center justify-between">
                  <div>
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Usuários
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                      Gerencie usuários e suas permissões no sistema.
              </Text>
            </div>
                  
                  <PermissionGuard moduleName="configuracoes_usuarios" permissionType="insert">
              <Button 
                      onClick={handleOpenCreateModal}
                      className="flex items-center space-x-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                      <PlusIcon className="h-5 w-5" />
                      <span>Novo Usuário</span>
              </Button>
                  </PermissionGuard>
          </div>
        </div>

              {/* Componente de Tabs */}
              <div className="mb-6">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab('usuarios')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'usuarios'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
                      }`}
                    >
                      Usuários
                    </button>
                    <button
                      onClick={() => setActiveTab('gestao-grupos')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'gestao-grupos'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
                      }`}
                    >
                      Grupos
                    </button>
                    <button
                      onClick={() => setActiveTab('organizacoes')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'organizacoes'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
                      }`}
                    >
                      Organizações
                    </button>
                    <button
                      onClick={() => setActiveTab('permissoes')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'permissoes'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
                      }`}
                    >
                      Permissões
                    </button>
                  </nav>
                </div>
              </div>

              {/* Conteúdo da Aba de Usuários */}
              {activeTab === 'usuarios' && (
                <>
              {/* Indicadores de Estatísticas */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {users.length}
                    </Text>
                    <Text size="sm" className="text-white">Total de Usuários</Text>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {users.filter(u => u.is_active).length}
                    </Text>
                    <Text size="sm" className="text-white">Usuários Ativos</Text>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {users.filter(u => !u.is_active).length}
                    </Text>
                    <Text size="sm" className="text-white">Usuários Inativos</Text>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {userGroups.length}
                    </Text>
                    <Text size="sm" className="text-white">Grupos</Text>
                  </div>
                </div>
              </div>

              {/* Filtros e Busca */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Busca */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Busca
                    </label>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Buscar usuários..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
              </div>

                  {/* Filtro de Status */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                    >
                      <option value="all">Todos os Status</option>
                      <option value="active">Ativos</option>
                      <option value="inactive">Inativos</option>
                    </Select>
                  </div>

                  {/* Filtro de Organização */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Organização
                    </label>
                    <Select
                      value={organizationFilter}
                      onChange={(e) => setOrganizationFilter(e.target.value)}
                    >
                      <option value="all">Todas as Organizações</option>
                      <option value="none">Sem Organização</option>
                      {organizations.map(organization => (
                        <option key={organization.id} value={organization.id}>
                          {organization.display_name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {/* Filtro de Grupo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Grupo
                    </label>
                    <Select
                      value={groupFilter}
                      onChange={(e) => setGroupFilter(e.target.value)}
                    >
                      <option value="all">Todos os Grupos</option>
                      {userGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.display_name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {/* Filtro de Grupo (Portal Atendimento) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Grupo (Portal Atendimento)
                    </label>
                    <Select
                      value={helpdeskGroupFilter}
                      onChange={(e) => setHelpdeskGroupFilter(e.target.value)}
                    >
                      <option value="all">Todos os Grupos (Portal)</option>
                      <option value="none">Sem Grupo (Portal)</option>
                      {helpdeskUserGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.display_name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {/* Botão Limpar Filtros */}
                  <div className="flex items-end">
                    <Button
                      onClick={() => {
                        setSearchTerm('')
                        setStatusFilter('all')
                        setOrganizationFilter('all')
                        setGroupFilter('all')
                        setHelpdeskGroupFilter('all')
                      }}
                      variant="outline"
                      className="h-9 px-3 text-sm flex items-center justify-center space-x-1.5"
                    >
                      <FunnelIcon className="h-4 w-4" />
                      <span>Limpar</span>
                    </Button>
                  </div>
            </div>
              </div>

              {/* Tabela de Usuários */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm min-w-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando usuários...</p>
            </div>
                ) : error ? (
                  <div className="p-8 text-center">
                    <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                    <Button
                      onClick={loadUsers}
                      className="mt-4"
                      style={{ backgroundColor: BRAND_COLOR }}
                    >
                      Tentar Novamente
                    </Button>
              </div>
                ) : (
                  <div className="min-w-0">
                    <div className="mb-2 flex items-center justify-end gap-2 px-4 pt-4">
                      <button
                        type="button"
                        onClick={() => scrollUsersListBy(-320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a esquerda"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollUsersListBy(320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a direita"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div
                      ref={usersListScrollRef}
                      className="w-full max-w-full overflow-x-auto"
                      onWheel={handleUsersListWheel}
                    >
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                      <SortableTableHeader
                            sortKey="name"
                            currentSort={sortBy}
                            sortOrder={sortOrder}
                            onSort={toggleSort}
                            className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-700"
                          >
                            Nome
                          </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="organization"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                        >
                          Organização
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="group"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                        >
                          Grupo
                        </SortableTableHeader>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Grupo (Portal Atendimento)
                        </th>
                        <SortableTableHeader
                          sortKey="status"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                        >
                          Status
                        </SortableTableHeader>
                          <SortableTableHeader
                            sortKey="created_at"
                            currentSort={sortBy}
                            sortOrder={sortOrder}
                            onSort={toggleSort}
                          >
                            Criado em
                          </SortableTableHeader>
                          <SortableTableHeader
                            sortKey="last_login"
                            currentSort={sortBy}
                            sortOrder={sortOrder}
                            onSort={toggleSort}
                          >
                            Último Acesso
                          </SortableTableHeader>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {usersPaginados.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap sticky left-0 z-10 bg-gray-50 dark:bg-gray-700">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {avatarUrls[user.id] && !failedAvatars.has(user.id) ? (
                                <img 
                                  src={avatarUrls[user.id]!} 
                                  alt={user.name}
                                  className="h-10 w-10 rounded-full object-cover"
                                  onError={() => {
                                    // Se a imagem falhar ao carregar, marcar como falhada para mostrar iniciais
                                    setFailedAvatars(prev => new Set(prev).add(user.id))
                                  }}
                                />
                              ) : (
                                <div 
                                  className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold ${getAvatarColor(user.name || user.email || '')}`}
                                >
                                  {getInitials(user.name || user.email || 'N')}
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {user.name}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {user.phone || 'Sem telefone'}
                                  </div>
                            </div>
                          </div>
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {user.organization?.display_name || 'Sem organização'}
                                </span>
                              </div>
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {user.user_group?.display_name || 'Sem grupo'}
                                </span>
                              </div>
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {user.helpdesk_user_group?.display_name || 'Sem grupo'}
                                </span>
                              </div>
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.is_active 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {user.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(user.created_at).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {user.last_login 
                                ? new Date(user.last_login).toLocaleDateString('pt-BR') 
                                : 'Nunca acessou'
                              }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="relative action-menu-container">
                                <Button
                                  onClick={() => setOpenActionMenu(openActionMenu === user.id ? null : user.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                  <EllipsisVerticalIcon className="h-5 w-5" />
                                </Button>
                                
                                {openActionMenu === user.id && (
                                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                                    <div className="py-1">
                                      <PermissionGuard moduleName="configuracoes_usuarios" permissionType="show">
                                        <button
                                          onClick={() => {
                                            openViewModal(user)
                                            setOpenActionMenu(null)
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                        >
                                          <EyeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                          <span>Visualizar</span>
                                        </button>
                                      </PermissionGuard>
                                      
                                      <PermissionGuard moduleName="configuracoes_usuarios" permissionType="update">
                                        <button
                                          onClick={() => {
                                            openEditModal(user)
                                            setOpenActionMenu(null)
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                        >
                                          <PencilIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                                          <span>Editar</span>
                                        </button>
                                      </PermissionGuard>
                                      
                                      {(!user.is_active || !user.last_login) && (
                                        <button
                                          onClick={() => {
                                            handleResendConfirmation(user)
                                            setOpenActionMenu(null)
                                          }}
                                          disabled={isLoading[`resend-${user.id}`] || !user.email}
                                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          <EnvelopeIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                          <span>Reenviar confirmação</span>
                                        </button>
                                      )}
                                      
                                      <PermissionGuard moduleName="configuracoes_usuarios" permissionType="delete">
                                        <button
                                          onClick={() => {
                                            handleDeleteUser(user.id)
                                            setOpenActionMenu(null)
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                          <span>Excluir</span>
                                        </button>
                                      </PermissionGuard>
                                      
                                      <button
                                        onClick={() => {
                                          openPermissionsModal(user)
                                          setOpenActionMenu(null)
                                        }}
                                        disabled
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 opacity-50 cursor-not-allowed"
                                      >
                                        <ShieldCheckIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                        <span>Permissões</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                    </div>
                
                {/* Controles de Paginação */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, filteredAndSortedUsers.length)} de {filteredAndSortedUsers.length} registros
                        </Text>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number;
                            if (totalPages <= 5) {
                              pageNumber = i + 1;
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i;
                            } else {
                              pageNumber = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                    
                    {filteredAndSortedUsers.length === 0 && (
                      <div className="text-center py-8">
                        <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          Nenhum usuário encontrado.
                        </p>
              </div>
                    )}
            </div>
                )}
          </div>

                </>
              )}

              {/* Conteúdo da Aba de Gestão de Grupos */}
              {activeTab === 'gestao-grupos' && (
                <>
                  {/* Header da seção */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                          Grupos
                        </Heading>
                        <Text size="sm" color="muted" className="mt-2">
                          Gerencie grupos de usuários e suas configurações.
                        </Text>
                      </div>
                      
                      <Button 
                        onClick={openCreateGroupModal}
                        className="flex items-center space-x-2"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <PlusIcon className="h-5 w-5" />
                        <span>Novo Grupo</span>
                      </Button>
                    </div>
                  </div>

                  {/* Listagem de Grupos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {userGroups.map((group) => {
                      const groupUsers = getUsersByGroup(group.id)
                      const isProtected = isProtectedGroup(group.name)
                      
                      return (
                        <div 
                          key={group.id} 
                          className={`border-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ${
                            group.is_admin 
                              ? 'border-purple-300 bg-purple-50 dark:border-purple-600 dark:bg-purple-900/20' 
                              : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                          }`}
                        >
                          {/* Header do Card */}
                          <div className="p-6 border-b-2 border-gray-200 dark:border-gray-600">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {group.display_name}
                                  </h3>
                                  {group.is_admin && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                      Admin
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                  {group.description || 'Sem descrição'}
                                </p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                  <div className="flex items-center space-x-1">
                                    <UserGroupIcon className="h-4 w-4" />
                                    <span>{groupUsers.length} usuário(s)</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <span className={`w-2 h-2 rounded-full ${
                                      group.is_active ? 'bg-green-500' : 'bg-red-500'
                                    }`}></span>
                                    <span>{group.is_active ? 'Ativo' : 'Inativo'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Ações do Card */}
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/30">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Button
                                  onClick={() => openEditGroupModal(group)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                  disabled={isProtected && group.name !== 'admin'}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                                
                                <Button
                                  onClick={() => openDeleteGroupModal(group)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  disabled={isProtected}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {isProtected && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  Grupo protegido
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Estado vazio */}
                  {userGroups.length === 0 && (
                    <div className="text-center py-12">
                      <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Nenhum grupo encontrado
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 mb-6">
                        Crie seu primeiro grupo para começar a organizar os usuários.
                      </p>
                      <Button 
                        onClick={openCreateGroupModal}
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Criar Primeiro Grupo
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Conteúdo da Aba de Organizações */}
              {activeTab === 'organizacoes' && (
                <>
                  {/* Header da seção */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                          Organizações
                        </Heading>
                        <Text size="sm" color="muted" className="mt-2">
                          Gerencie organizações e suas configurações.
                        </Text>
                      </div>
                      
                      <Button 
                        onClick={openCreateOrganizationModal}
                        className="flex items-center space-x-2"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <PlusIcon className="h-5 w-5" />
                        <span>Nova Organização</span>
                      </Button>
                    </div>
                  </div>

                  {/* Listagem de Organizações */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {organizations.map((organization) => {
                      const organizationUsers = getUsersByOrganization(organization.id)
                      
                      return (
                        <div 
                          key={organization.id} 
                          className="border-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
                        >
                          {/* Header do Card */}
                          <div className="p-6 border-b-2 border-gray-200 dark:border-gray-600">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {organization.display_name}
                                  </h3>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                  {organization.description || 'Sem descrição'}
                                </p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                  <div className="flex items-center space-x-1">
                                    <UserGroupIcon className="h-4 w-4" />
                                    <span>{organizationUsers.length} usuário(s)</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <span className={`w-2 h-2 rounded-full ${
                                      organization.is_active ? 'bg-green-500' : 'bg-red-500'
                                    }`}></span>
                                    <span>{organization.is_active ? 'Ativo' : 'Inativo'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Ações do Card */}
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/30">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Button
                                  onClick={() => openEditOrganizationModal(organization)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                                
                                <Button
                                  onClick={() => openDeleteOrganizationModal(organization)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Estado vazio */}
                  {organizations.length === 0 && (
                    <div className="text-center py-12">
                      <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Nenhuma organização encontrada
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 mb-6">
                        Crie sua primeira organização para começar a organizar os usuários.
                      </p>
                      <Button 
                        onClick={openCreateOrganizationModal}
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Criar Primeira Organização
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Conteúdo da Aba de Permissões */}
              {activeTab === 'permissoes' && (
                <>

                  {/* Matriz Interativa de Permissões */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Matriz de Permissões por Grupo
                      </h3>
                      
                      {/* Filtros */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Filtrar por Grupo
                          </label>
                          <select
                            value={matrixGroupFilter}
                            onChange={(e) => setMatrixGroupFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="all">Todos os grupos</option>
                            {userGroups.map(group => (
                              <option key={group.id} value={group.id}>
                                {group.display_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Filtrar por Módulo
                          </label>
                          <select
                            value={matrixModuleFilter}
                            onChange={(e) => setMatrixModuleFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="all">Todos os módulos</option>
                            {allModules.map(module => (
                              <option key={module.id} value={module.id}>
                                {module.display_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          {(matrixGroupFilter !== 'all' || matrixModuleFilter !== 'all') && (
                            <button
                              onClick={() => {
                                setMatrixGroupFilter('all')
                                setMatrixModuleFilter('all')
                              }}
                              className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                            >
                              Limpar filtros
                            </button>
                          )}
                          
                          <div className="flex gap-2">
                            <button
                              onClick={expandAllGroups}
                              className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:text-green-400 rounded-md font-medium transition-colors"
                            >
                              Expandir Todos
                            </button>
                            <button
                              onClick={collapseAllGroups}
                              className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 rounded-md font-medium transition-colors"
                            >
                              Retrair Todos
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Seções Expansíveis por Grupo */}
                    <div className="space-y-3">
                      {userGroups.filter(group => matrixGroupFilter === 'all' || group.id === matrixGroupFilter).map((group) => {
                        const groupUsers = getUsersByGroup(group.id)
                        const groupPermissionsList = groupPermissions[group.id] || []
                        const isExpanded = expandedGroups.has(group.id)
                        const filteredModules = allModules
                          .filter(module => matrixModuleFilter === 'all' || module.id === matrixModuleFilter)
                          .sort((a, b) => {
                            // Ordenar pelo order_index (módulos sem order_index vão para o final)
                            const aOrder = a.order_index ?? 1e9
                            const bOrder = b.order_index ?? 1e9
                            return aOrder - bOrder
                          })
                        
                        // Calcular estatísticas do grupo
                        const modulesWithAccess = groupPermissionsList.length
                        const totalModules = filteredModules.length
                        
                        return (
                          <div key={group.id} className="border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-md bg-white dark:bg-gray-800">
                            {/* Header da Seção */}
                            <div 
                              className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                              onClick={() => toggleGroupExpansion(group.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="flex items-center space-x-2">
                                    <UserGroupIcon className="h-5 w-5 text-gray-500" />
                                    <span className="font-medium text-gray-900 dark:text-white text-lg">
                                      {group.display_name}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                      {groupUsers.length} usuários
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {modulesWithAccess} de {totalModules} módulos com acesso
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {isExpanded ? 'Retrair' : 'Expandir'}
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUpIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                                  ) : (
                                    <ChevronDownIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Conteúdo Expansível */}
                            {isExpanded && (
                              <div className="border-t-2 border-gray-300 dark:border-gray-600 transition-all duration-300 bg-gray-50 dark:bg-gray-700/30">
                                {/* Abas */}
                                <div className="border-b-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600/50">
                                  <nav className="flex space-x-8 px-4" aria-label="Tabs">
                                    <button
                                      onClick={() => setGroupActiveTab(group.id, 'permissions')}
                                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                                        (groupActiveTabs[group.id] || 'permissions') === 'permissions'
                                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                      }`}
                                    >
                                      Permissões
                                    </button>
                                    <button
                                      onClick={() => setGroupActiveTab(group.id, 'users')}
                                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                                        groupActiveTabs[group.id] === 'users'
                                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                      }`}
                                    >
                                      Usuários ({groupUsers.length})
                                    </button>
                                    <button
                                      onClick={() => setGroupActiveTab(group.id, 'wiki')}
                                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                                        groupActiveTabs[group.id] === 'wiki'
                                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                      }`}
                                    >
                                      Wiki
                                    </button>
                                  </nav>
                                </div>
                                
                                {/* Conteúdo das Abas */}
                                <div className="p-6 bg-gray-50 dark:bg-gray-700/30">
                                  {(groupActiveTabs[group.id] || 'permissions') === 'permissions' ? (
                                    /* Aba Permissões — layout hierárquico por módulo-pai */
                                    <div className="space-y-3">
                                      {(() => {
                                        const tree = buildModuleTree(filteredModules)
                                        return tree.map((parentModule) => {
                                          const children = (parentModule as any).children as Module[]
                                          const allItems = children.length > 0 ? children : [parentModule]
                                          const isRootOnly = children.length === 0

                                          // Calcular resumo de acesso dos filhos (ou do próprio root)
                                          const accessCounts = { none: 0, view: 0, partial: 0, full: 0 }
                                          allItems.forEach((mod) => {
                                            const perm = groupPermissionsList.find(p => p.module_id === mod.id)
                                            const level = perm ? getAccessLevel(perm) : 'none'
                                            accessCounts[level]++
                                          })
                                          const totalItems = allItems.length
                                          const withAccess = totalItems - accessCounts.none

                                          return (
                                            <div key={parentModule.id} className="rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
                                              {/* Cabeçalho do módulo-pai — mesmo estilo dos filhos */}
                                              {!isRootOnly && (() => {
                                                const parentPerms = groupPermissionsList.find(p => p.module_id === parentModule.id)
                                                const parentLevel = parentPerms ? getAccessLevel(parentPerms) : 'none'
                                                const levelColors = {
                                                  none:    'bg-gray-100 dark:bg-gray-700/60',
                                                  view:    'bg-gray-100 dark:bg-gray-700/60',
                                                  partial: 'bg-gray-100 dark:bg-gray-700/60',
                                                  full:    'bg-gray-100 dark:bg-gray-700/60',
                                                }
                                                const dotColors = {
                                                  none:    'bg-red-400',
                                                  view:    'bg-blue-400',
                                                  partial: 'bg-yellow-400',
                                                  full:    'bg-green-500',
                                                }
                                                const levelLabel = {
                                                  none:    'Sem acesso',
                                                  view:    'Só visualizar',
                                                  partial: 'Parcial',
                                                  full:    'Acesso total',
                                                }
                                                return (
                                                  <div
                                                    className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors hover:brightness-95 dark:hover:brightness-110 border-b border-gray-200 dark:border-gray-600 ${levelColors[parentLevel]}`}
                                                    onClick={() => openPermissionModal(group.id, parentModule.id)}
                                                  >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[parentLevel]}`} />
                                                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                                                        {parentModule.display_name}
                                                      </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                                      <span className={`text-xs font-medium ${
                                                        parentLevel === 'none' ? 'text-red-500 dark:text-red-400' :
                                                        parentLevel === 'view' ? 'text-blue-600 dark:text-blue-400' :
                                                        parentLevel === 'partial' ? 'text-yellow-600 dark:text-yellow-400' :
                                                        'text-green-600 dark:text-green-400'
                                                      }`}>
                                                        {levelLabel[parentLevel]}
                                                      </span>
                                                      {parentPerms && (
                                                        <div className="flex gap-1">
                                                          {parentPerms.can_show && (
                                                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 text-xs rounded">Ver</span>
                                                          )}
                                                          {parentPerms.can_view_values && (
                                                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-xs rounded">Valores</span>
                                                          )}
                                                          {parentPerms.can_insert && (
                                                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 text-xs rounded">Criar</span>
                                                          )}
                                                          {parentPerms.can_update && (
                                                            <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 text-xs rounded">Editar</span>
                                                          )}
                                                          {parentPerms.can_delete && (
                                                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 text-xs rounded">Excluir</span>
                                                          )}
                                                        </div>
                                                      )}
                                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-1 ${
                                                        withAccess === 0
                                                          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                          : withAccess === totalItems
                                                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                                                      }`}>
                                                        {withAccess}/{totalItems} filhos
                                                      </span>
                                                      <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 -rotate-90" />
                                                    </div>
                                                  </div>
                                                )
                                              })()}

                                              {/* Linhas dos módulos filhos (ou root sem filhos) */}
                                              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                {allItems.map((mod, idx) => {
                                                  const perms = groupPermissionsList.find(p => p.module_id === mod.id)
                                                  const level = perms ? getAccessLevel(perms) : 'none'
                                                  const levelColors = isRootOnly ? {
                                                    none:    'bg-gray-100 dark:bg-gray-700/60',
                                                    view:    'bg-gray-100 dark:bg-gray-700/60',
                                                    partial: 'bg-gray-100 dark:bg-gray-700/60',
                                                    full:    'bg-gray-100 dark:bg-gray-700/60',
                                                  } : {
                                                    none:    'bg-red-50 dark:bg-red-900/10',
                                                    view:    'bg-blue-50 dark:bg-blue-900/10',
                                                    partial: 'bg-yellow-50 dark:bg-yellow-900/10',
                                                    full:    'bg-green-50 dark:bg-green-900/10',
                                                  }
                                                  const dotColors = {
                                                    none:    'bg-red-400',
                                                    view:    'bg-blue-400',
                                                    partial: 'bg-yellow-400',
                                                    full:    'bg-green-500',
                                                  }
                                                  const levelLabel = {
                                                    none:    'Sem acesso',
                                                    view:    'Só visualizar',
                                                    partial: 'Parcial',
                                                    full:    'Acesso total',
                                                  }
                                                  return (
                                                    <div
                                                      key={mod.id}
                                                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors hover:brightness-95 dark:hover:brightness-110 ${levelColors[level]} ${isRootOnly && idx === 0 ? '' : 'pl-8'}`}
                                                      onClick={() => openPermissionModal(group.id, mod.id)}
                                                    >
                                                      <div className="flex items-center gap-2 min-w-0">
                                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[level]}`} />
                                                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                                          {mod.display_name}
                                                        </span>
                                                      </div>
                                                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                                        <span className={`text-xs font-medium ${
                                                          level === 'none' ? 'text-red-500 dark:text-red-400' :
                                                          level === 'view' ? 'text-blue-600 dark:text-blue-400' :
                                                          level === 'partial' ? 'text-yellow-600 dark:text-yellow-400' :
                                                          'text-green-600 dark:text-green-400'
                                                        }`}>
                                                          {levelLabel[level]}
                                                        </span>
                                                        {perms && (
                                                          <div className="flex gap-1">
                                                            {perms.can_show && (
                                                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 text-xs rounded">Ver</span>
                                                            )}
                                                            {perms.can_view_values && (
                                                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-xs rounded">Valores</span>
                                                            )}
                                                            {perms.can_insert && (
                                                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 text-xs rounded">Criar</span>
                                                            )}
                                                            {perms.can_update && (
                                                              <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 text-xs rounded">Editar</span>
                                                            )}
                                                            {perms.can_delete && (
                                                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 text-xs rounded">Excluir</span>
                                                            )}
                                                          </div>
                                                        )}
                                                        <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 -rotate-90" />
                                                      </div>
                                                    </div>
                                                  )
                                                })}
                                              </div>
                                            </div>
                                          )
                                        })
                                      })()}
                                    </div>
                                  ) : groupActiveTabs[group.id] === 'users' ? (
                                    /* Aba Usuários */
                                    <div className="space-y-3">
                                      {groupUsers.length > 0 ? (
                                        groupUsers.map((user) => (
                                          <div key={user.id} className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-600/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                            <div className="flex items-center space-x-3">
                                              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                                                <UserIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                              </div>
                                              <div>
                                                <div className="font-medium text-gray-900 dark:text-white">
                                                  {user.name}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                  {user.phone}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                user.is_active 
                                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                              }`}>
                                                {user.is_active ? 'Ativo' : 'Inativo'}
                                              </span>
                                              <button
                                                onClick={() => {
                                                  setSelectedUser(user)
                                                  setIsViewModalOpen(true)
                                                }}
                                                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                title="Ver detalhes"
                                              >
                                                <EyeIcon className="h-4 w-4" />
                                              </button>
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                          <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                                          <p>Nenhum usuário neste grupo</p>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    /* Aba Wiki - Departamentos por grupo */
                                    <div>
                                      <div className="mb-3">
                                        <Text size="sm" color="muted">Selecione os departamentos da Wiki com acesso para este grupo.</Text>
                                      </div>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {allDepartments.map(dept => {
                                          const setForGroup = groupDeptIdsByGroup[group.id] || new Set<string>()
                                          const checked = setForGroup.has(dept.id)
                                          return (
                                            <label key={dept.id} className="flex items-center gap-2 bg-white dark:bg-gray-600 rounded-lg p-3 border cursor-pointer">
                                              <input
                                                type="checkbox"
                                                className="rounded border-gray-300"
                                                checked={checked}
                                                disabled={loadingGroupDepts}
                                                onChange={(e) => toggleGroupDepartment(group.id, dept.id, e.target.checked)}
                                              />
                                              <span className="text-sm text-gray-800 dark:text-gray-100">{dept.name}</span>
                                            </label>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    
                    {/* Legenda no rodapé */}
                    <div className="mt-6 pt-4 border-t-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-center space-x-6 text-sm">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-red-500 rounded"></div>
                          <span className="text-gray-600 dark:text-gray-400">Sem acesso</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-blue-500 rounded"></div>
                          <span className="text-gray-600 dark:text-gray-400">Visualização (apenas leitura)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                          <span className="text-gray-600 dark:text-gray-400">Parcial (algumas ações)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-green-500 rounded"></div>
                          <span className="text-gray-600 dark:text-gray-400">Total (todas as ações)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </PermissionGuard>
        </main>
      </div>

      {/* Modal de Criação */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                         <div className="flex items-center justify-between mb-4">
               <Heading as="h3" size="2xl">Novo Usuário</Heading>
               <Button
                 onClick={() => setIsCreateModalOpen(false)}
                 variant="ghost"
                 size="sm"
               >
                 <XMarkIcon className="h-5 w-5" />
               </Button>
              </div>

             <div className="space-y-4">
                    {/* Exibir mensagens de erro */}
                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                          <div className="ml-3">
                            <p className="text-sm text-red-800 dark:text-red-200">
                              {error}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Nome
                      </label>
                      <Input
                   value={createForm.name}
                   onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                   placeholder="Nome completo"
                      />
                    </div>
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Email
                      </label>
                      <Input
                   type="email"
                   value={createForm.email}
                   onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                   placeholder="usuario@exemplo.com"
                      />
                    </div>
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Telefone
                      </label>
                      <Input
                   value={createForm.phone}
                   onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                        placeholder="(11) 99999-9999"
                      />
                    </div>
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Organização
                      </label>
                      <Select
                   value={createForm.organization_id}
                   onChange={(e) => setCreateForm({...createForm, organization_id: e.target.value})}
                      >
                        <option value="">Selecione uma organização</option>
                   {organizations.map(organization => (
                     <option key={organization.id} value={organization.id}>
                       {organization.display_name}
                          </option>
                        ))}
                      </Select>
                    </div>
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Grupo
                      </label>
                      <Select
                   value={createForm.user_group_id}
                   onChange={(e) => setCreateForm({...createForm, user_group_id: e.target.value})}
                      >
                        <option value="">Selecione um grupo</option>
                   {userGroups.map(group => (
                     <option key={group.id} value={group.id}>
                       {group.display_name}
                          </option>
                        ))}
                      </Select>
                    </div>
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Senha
                      </label>
                 <Input
                   type="password"
                   value={createForm.password}
                   onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                   placeholder="Senha"
                 />
                </div>

                <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Confirmar Senha
                 </label>
                 <Input
                   type="password"
                   value={createForm.confirmPassword}
                   onChange={(e) => setCreateForm({...createForm, confirmPassword: e.target.value})}
                   placeholder="Confirmar senha"
                 />
                  </div>
                </div>

            <div className="flex justify-end space-x-3 mt-6">
                  <Button
                onClick={() => setIsCreateModalOpen(false)}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                  <Button
                onClick={() => {
                  if (!isCreatingUser) {
                    handleCreateUser()
                  }
                }}
                    style={{ backgroundColor: BRAND_COLOR }}
                    disabled={isCreatingUser}
                  >
                    {isCreatingUser ? 'Criando...' : 'Criar Usuário'}
                  </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                         <div className="flex items-center justify-between mb-4">
               <Heading as="h3" size="2xl">Editar Usuário</Heading>
               <Button
                 onClick={() => setIsEditModalOpen(false)}
                 variant="ghost"
                 size="sm"
               >
                 <XMarkIcon className="h-5 w-5" />
               </Button>
              </div>

             <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Nome
                      </label>
                      <Input
                   value={editForm.name}
                   onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                   placeholder="Nome completo"
                      />
                    </div>
               
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Telefone
                      </label>
                      <Input
                   value={editForm.phone}
                   onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        placeholder="(11) 99999-9999"
                      />
                    </div>
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Organização
                      </label>
                      <Select
                   value={editForm.organization_id}
                   onChange={(e) => setEditForm({...editForm, organization_id: e.target.value})}
                      >
                        <option value="">Selecione uma organização</option>
                   {organizations.map(organization => (
                     <option key={organization.id} value={organization.id}>
                       {organization.display_name}
                          </option>
                        ))}
                      </Select>
                    </div>
               
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Grupo
                      </label>
                      <Select
                   value={editForm.user_group_id}
                   onChange={(e) => setEditForm({...editForm, user_group_id: e.target.value})}
                      >
                        <option value="">Selecione um grupo</option>
                   {userGroups.map(group => (
                     <option key={group.id} value={group.id}>
                       {group.display_name}
                          </option>
                        ))}
                      </Select>
                </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Grupo (Portal Atendimento)
                      </label>
                      <Select
                   value={editForm.helpdesk_user_group_id}
                   onChange={(e) => setEditForm({...editForm, helpdesk_user_group_id: e.target.value})}
                      >
                        <option value="">Sem grupo</option>
                   {helpdeskUserGroups.map(group => (
                     <option key={group.id} value={group.id}>
                       {group.display_name}
                          </option>
                        ))}
                      </Select>
                </div>

              <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                  id="is_active"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
                  Usuário ativo
                      </label>
                  </div>
                </div>

            <div className="flex justify-end space-x-3 mt-6">
                  <Button
                onClick={() => setIsEditModalOpen(false)}
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                    <Button
                onClick={handleEditUser}
                      style={{ backgroundColor: BRAND_COLOR }}
                    >
                      Salvar Alterações
                    </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {isViewModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
               <Heading as="h3" size="2xl">Detalhes do Usuário</Heading>
              <Button
                onClick={() => setIsViewModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
              </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Informações Básicas</h4>
                <div className="space-y-3">
                    <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Nome</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedUser?.name}</p>
                    </div>
                    <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">E-mail</label>
                    <p className="text-sm text-gray-900 dark:text-white break-all">{selectedUser?.email || 'Não informado'}</p>
                    </div>
                    <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">UUID</label>
                    <p className="text-sm text-gray-900 dark:text-white font-mono break-all">{selectedUser?.id}</p>
                    </div>
                    <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Telefone</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedUser?.phone || 'Não informado'}</p>
                    </div>
                    <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Grupo</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedUser?.user_group?.display_name || 'Sem grupo'}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Informações do Sistema</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedUser?.is_active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {selectedUser?.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Criado em</label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedUser?.created_at ? new Date(selectedUser.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                    </p>
                    </div>
                    <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Último acesso</label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedUser?.last_login 
                        ? new Date(selectedUser.last_login).toLocaleDateString('pt-BR') 
                        : 'Nunca acessou'
                      }
                    </p>
                  </div>
                    </div>
                  </div>
                </div>

            <div className="mt-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Permissões Específicas</h4>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                {userPermissions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {userPermissions.map(permission => (
                      <div key={permission.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-600 rounded-lg">
                <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {permission.module?.display_name || permission.module_id}
                          </p>
                          <div className="flex space-x-2 mt-1">
                            {permission.can_show && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Ver</span>}
                            {permission.can_view_values && <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Valores</span>}
                            {permission.can_insert && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Criar</span>}
                            {permission.can_update && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Editar</span>}
                            {permission.can_delete && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Excluir</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                    Nenhuma permissão específica configurada. Usando permissões do grupo.
                  </p>
                )}
                  </div>
                </div>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setIsViewModalOpen(false)}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                Fechar
              </Button>
                </div>
          </div>
        </div>
      )}

      {/* Modal de Permissões */}
      {isPermissionsModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
                         <div className="flex items-center justify-between mb-4">
               <Heading as="h3" size="2xl">Permissões do Usuário</Heading>
              <Button
                onClick={() => setIsPermissionsModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configurando permissões específicas para <strong>{selectedUser?.name}</strong>
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                As permissões específicas do usuário têm precedência sobre as permissões do grupo.
              </p>

              {/* Abas do modal de permissões: Módulos | Wiki */}
              <div className="border-b border-gray-300 dark:border-gray-600 mb-4">
                <nav className="flex space-x-6" aria-label="Tabs">
                  <button
                    onClick={() => setPermissionsModalTab('modules')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      permissionsModalTab === 'modules'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Módulos
                  </button>
                  <button
                    onClick={() => setPermissionsModalTab('wiki')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      permissionsModalTab === 'wiki'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Wiki
                  </button>
                </nav>
              </div>

              {permissionsModalTab === 'modules' && (
                <div className="space-y-3">
                  {buildModuleTree(allModules).map((parentModule) => {
                    const children = (parentModule as any).children as Module[]
                    const allItems = children.length > 0 ? children : [parentModule]
                    const isRootOnly = children.length === 0

                    return (
                      <div key={parentModule.id} className="rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
                        {/* Cabeçalho do módulo-pai */}
                        {!isRootOnly && (
                          <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                            <ShieldCheckIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                            <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                              {parentModule.display_name}
                            </span>
                          </div>
                        )}

                        {/* Linha de cabeçalho das colunas */}
                        <div className={`hidden md:grid md:grid-cols-[1fr_repeat(5,56px)] gap-0 px-4 py-1.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-100 dark:border-gray-700 ${!isRootOnly ? '' : ''}`}>
                          <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Módulo</span>
                          {(['Ver','Valores','Criar','Editar','Excluir'] as const).map(lbl => (
                            <span key={lbl} className="text-xs text-gray-400 dark:text-gray-500 font-medium text-center">{lbl}</span>
                          ))}
                        </div>

                        {/* Linhas dos filhos */}
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {allItems.map((mod) => {
                            const mp = tempPermissions[mod.id] || {
                              can_show: false, can_insert: false, can_update: false,
                              can_delete: false, can_view_values: false
                            }
                            const hasAny = mp.can_show || mp.can_insert || mp.can_update || mp.can_delete
                            return (
                              <div
                                key={mod.id}
                                className={`grid grid-cols-1 md:grid-cols-[1fr_repeat(5,56px)] gap-0 items-center px-4 py-3 transition-colors ${
                                  hasAny ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/60 dark:bg-gray-800/40'
                                } ${!isRootOnly ? 'pl-8' : ''}`}
                              >
                                <div className="flex items-center gap-2 mb-2 md:mb-0">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${hasAny ? 'bg-green-400' : 'bg-gray-300 dark:bg-gray-600'}`} />
                                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                    {mod.display_name}
                                  </span>
                                </div>

                                {/* Mobile: labels inline */}
                                <div className="md:hidden grid grid-cols-2 gap-2 mt-1 pl-4">
                                  {([ 
                                    { label: 'Visualizar', key: 'can_show' as const, disabled: false },
                                    { label: 'Ver valores', key: 'can_view_values' as const, disabled: !mp.can_show, hidden: !VIEW_VALUES_ELIGIBLE_MODULES.has(mod.name) },
                                    { label: 'Criar', key: 'can_insert' as const, disabled: false },
                                    { label: 'Editar', key: 'can_update' as const, disabled: false },
                                    { label: 'Excluir', key: 'can_delete' as const, disabled: false },
                                  ] as { label: string; key: 'can_show'|'can_insert'|'can_update'|'can_delete'|'can_view_values'; disabled: boolean; hidden?: boolean }[]).filter(f => !f.hidden).map(field => (
                                    <label key={field.key} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={mp[field.key]}
                                        disabled={field.disabled}
                                        onChange={(e) => updateTempPermission(mod.id, field.key, e.target.checked)}
                                        className="rounded border-gray-300 disabled:opacity-40"
                                      />
                                      {field.label}
                                    </label>
                                  ))}
                                </div>

                                {/* Desktop: checkboxes centralizados nas colunas */}
                                <div className="hidden md:flex justify-center">
                                  <input type="checkbox" checked={mp.can_show}
                                    onChange={(e) => updateTempPermission(mod.id, 'can_show', e.target.checked)}
                                    className="rounded border-gray-300 cursor-pointer" />
                                </div>
                                <div className="hidden md:flex justify-center">
                                  {VIEW_VALUES_ELIGIBLE_MODULES.has(mod.name) ? (
                                    <input type="checkbox" checked={mp.can_view_values}
                                      disabled={!mp.can_show}
                                      onChange={(e) => updateTempPermission(mod.id, 'can_view_values', e.target.checked)}
                                      className="rounded border-gray-300 cursor-pointer disabled:opacity-40" />
                                  ) : (
                                    <span className="text-gray-200 dark:text-gray-700 text-xs">—</span>
                                  )}
                                </div>
                                <div className="hidden md:flex justify-center">
                                  <input type="checkbox" checked={mp.can_insert}
                                    onChange={(e) => updateTempPermission(mod.id, 'can_insert', e.target.checked)}
                                    className="rounded border-gray-300 cursor-pointer" />
                                </div>
                                <div className="hidden md:flex justify-center">
                                  <input type="checkbox" checked={mp.can_update}
                                    onChange={(e) => updateTempPermission(mod.id, 'can_update', e.target.checked)}
                                    className="rounded border-gray-300 cursor-pointer" />
                                </div>
                                <div className="hidden md:flex justify-center">
                                  <input type="checkbox" checked={mp.can_delete}
                                    onChange={(e) => updateTempPermission(mod.id, 'can_delete', e.target.checked)}
                                    className="rounded border-gray-300 cursor-pointer" />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {permissionsModalTab === 'wiki' && (
                <div>
                  <div className="mb-3">
                    <Text size="sm" color="muted">Selecione os departamentos da Wiki que o usuário poderá visualizar.</Text>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {allDepartments.map(dept => {
                      const checked = userDeptIds.has(dept.id)
                      return (
                        <label key={dept.id} className="flex items-center gap-2 bg-white dark:bg-gray-600 rounded-lg p-3 border cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={checked}
                            disabled={loadingUserDepts}
                            onChange={(e) => toggleUserDepartment(dept.id, e.target.checked)}
                          />
                          <span className="text-sm text-gray-800 dark:text-gray-100">{dept.name}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => setIsPermissionsModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
                                <Button
                onClick={savePermissions}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                Salvar Permissões
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Usuários do Grupo */}
      {isGroupUsersModalOpen && selectedGroupForUsers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Usuários do Grupo: {selectedGroupForUsers?.display_name}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  {selectedGroupForUsers?.description || 'Sem descrição'}
                </Text>
              </div>
              <Button
                onClick={() => setIsGroupUsersModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Estatísticas do Grupo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <div className="flex items-center">
                  <UserGroupIcon className="h-6 w-6 text-blue-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Total</p>
                    <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                      {selectedGroupForUsers ? getUsersByGroup(selectedGroupForUsers.id).length : 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">Ativos</p>
                    <p className="text-2xl font-semibold text-green-900 dark:text-green-100">
                      {selectedGroupForUsers ? getUsersByGroup(selectedGroupForUsers.id).filter(u => u.is_active).length : 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <div className="flex items-center">
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">Inativos</p>
                    <p className="text-2xl font-semibold text-red-900 dark:text-red-100">
                      {selectedGroupForUsers ? getUsersByGroup(selectedGroupForUsers.id).filter(u => !u.is_active).length : 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Usuários */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Lista de Usuários
                </h4>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Usuário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Último Acesso
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {selectedGroupForUsers ? getUsersByGroup(selectedGroupForUsers.id).map(user => (
                      <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_active 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {user.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {user.last_login 
                            ? new Date(user.last_login).toLocaleDateString('pt-BR')
                            : 'Nunca'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Button
                              onClick={() => {
                                setIsGroupUsersModalOpen(false)
                                openViewModal(user)
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                setIsGroupUsersModalOpen(false)
                                openEditModal(user)
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            {(!user.is_active || !user.last_login) && (
                              <Button
                                onClick={() => handleResendConfirmation(user)}
                                variant="ghost"
                                size="sm"
                                disabled={isLoading[`resend-${user.id}`] || !user.email}
                                className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                              >
                                <EnvelopeIcon className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : []}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setIsGroupUsersModalOpen(false)}
                variant="outline"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Grupo */}
      {isCreateGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">Novo Grupo</Heading>
              <Button
                onClick={() => setIsCreateGroupModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Interno
                </label>
                <Input
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
                  placeholder="nome_do_grupo"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Será convertido para lowercase e espaços substituídos por _
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome de Exibição
                </label>
                <Input
                  value={groupForm.display_name}
                  onChange={(e) => setGroupForm({...groupForm, display_name: e.target.value})}
                  placeholder="Nome do Grupo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({...groupForm, description: e.target.value})}
                  placeholder="Descrição do grupo..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_admin"
                  checked={groupForm.is_admin}
                  onChange={(e) => setGroupForm({...groupForm, is_admin: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="is_admin" className="text-sm text-gray-700 dark:text-gray-300">
                  É grupo de administradores
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={groupForm.is_active}
                  onChange={(e) => setGroupForm({...groupForm, is_active: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
                  Grupo ativo
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => setIsCreateGroupModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={createGroup}
                disabled={groupLoading.create || !groupForm.name || !groupForm.display_name}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {groupLoading.create ? 'Criando...' : 'Criar Grupo'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Grupo */}
      {isEditGroupModalOpen && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">Editar Grupo</Heading>
              <Button
                onClick={() => setIsEditGroupModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Interno
                </label>
                <Input
                  value={groupForm.name}
                  disabled
                  className="bg-gray-100 dark:bg-gray-700"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Nome interno não pode ser alterado
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome de Exibição
                </label>
                <Input
                  value={groupForm.display_name}
                  onChange={(e) => setGroupForm({...groupForm, display_name: e.target.value})}
                  placeholder="Nome do Grupo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({...groupForm, description: e.target.value})}
                  placeholder="Descrição do grupo..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit_is_admin"
                  checked={groupForm.is_admin}
                  onChange={(e) => setGroupForm({...groupForm, is_admin: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="edit_is_admin" className="text-sm text-gray-700 dark:text-gray-300">
                  É grupo de administradores
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit_is_active"
                  checked={groupForm.is_active}
                  onChange={(e) => setGroupForm({...groupForm, is_active: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="edit_is_active" className="text-sm text-gray-700 dark:text-gray-300">
                  Grupo ativo
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => setIsEditGroupModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={updateGroup}
                disabled={groupLoading.edit}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {groupLoading.edit ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Grupo */}
      {isDeleteGroupModalOpen && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-red-600 dark:text-red-400">
                Excluir Grupo
              </Heading>
              <Button
                onClick={() => setIsDeleteGroupModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Tem certeza que deseja excluir o grupo?
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Esta ação não pode ser desfeita.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {selectedGroup.display_name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedGroup.description || 'Sem descrição'}
                </p>
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                  <span>Usuários: {getUsersByGroup(selectedGroup.id).length}</span>
                  <span>Status: {selectedGroup.is_active ? 'Ativo' : 'Inativo'}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => setIsDeleteGroupModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={deleteGroup}
                disabled={groupLoading.delete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {groupLoading.delete ? 'Excluindo...' : 'Excluir Grupo'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Organização */}
      {isCreateOrganizationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">Nova Organização</Heading>
              <Button
                onClick={() => setIsCreateOrganizationModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Interno
                </label>
                <Input
                  value={organizationForm.name}
                  onChange={(e) => setOrganizationForm({...organizationForm, name: e.target.value})}
                  placeholder="nome_da_organizacao"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Será convertido para lowercase e espaços substituídos por _
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome de Exibição
                </label>
                <Input
                  value={organizationForm.display_name}
                  onChange={(e) => setOrganizationForm({...organizationForm, display_name: e.target.value})}
                  placeholder="Nome da Organização"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={organizationForm.description}
                  onChange={(e) => setOrganizationForm({...organizationForm, description: e.target.value})}
                  placeholder="Descrição da organização..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="org_is_active"
                  checked={organizationForm.is_active}
                  onChange={(e) => setOrganizationForm({...organizationForm, is_active: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="org_is_active" className="text-sm text-gray-700 dark:text-gray-300">
                  Organização ativa
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => setIsCreateOrganizationModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={createOrganization}
                disabled={organizationLoading.create || !organizationForm.name || !organizationForm.display_name}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {organizationLoading.create ? 'Criando...' : 'Criar Organização'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Organização */}
      {isEditOrganizationModalOpen && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">Editar Organização</Heading>
              <Button
                onClick={() => setIsEditOrganizationModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Interno
                </label>
                <Input
                  value={organizationForm.name}
                  disabled
                  className="bg-gray-100 dark:bg-gray-700"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Nome interno não pode ser alterado
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome de Exibição
                </label>
                <Input
                  value={organizationForm.display_name}
                  onChange={(e) => setOrganizationForm({...organizationForm, display_name: e.target.value})}
                  placeholder="Nome da Organização"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={organizationForm.description}
                  onChange={(e) => setOrganizationForm({...organizationForm, description: e.target.value})}
                  placeholder="Descrição da organização..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit_org_is_active"
                  checked={organizationForm.is_active}
                  onChange={(e) => setOrganizationForm({...organizationForm, is_active: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="edit_org_is_active" className="text-sm text-gray-700 dark:text-gray-300">
                  Organização ativa
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => setIsEditOrganizationModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={updateOrganization}
                disabled={organizationLoading.edit}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {organizationLoading.edit ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Organização */}
      {isDeleteOrganizationModalOpen && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-red-600 dark:text-red-400">
                Excluir Organização
              </Heading>
              <Button
                onClick={() => setIsDeleteOrganizationModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Tem certeza que deseja excluir a organização?
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Esta ação não pode ser desfeita.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {selectedOrganization.display_name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedOrganization.description || 'Sem descrição'}
                </p>
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                  <span>Usuários: {getUsersByOrganization(selectedOrganization.id).length}</span>
                  <span>Status: {selectedOrganization.is_active ? 'Ativo' : 'Inativo'}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => setIsDeleteOrganizationModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={deleteOrganization}
                disabled={organizationLoading.delete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {organizationLoading.delete ? 'Excluindo...' : 'Excluir Organização'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Permissões do Grupo */}
      {isPermissionModalOpen && selectedGroupForPermission && selectedModuleForPermission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Configurar Permissões
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  {selectedGroupForPermission?.display_name} - {selectedModuleForPermission?.display_name}
                </Text>
              </div>
              <Button
                onClick={() => setIsPermissionModalOpen(false)}
                variant="ghost"
                size="sm"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Botões para marcar/desmarcar todos */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
              <Text size="sm" className="text-gray-600 dark:text-gray-400">
                Seleção rápida:
              </Text>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSelectAllPermissions}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  Marcar Todos
                </Button>
                <Button
                  onClick={handleDeselectAllPermissions}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  Desmarcar Todos
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_show"
                  checked={editingPermissions.can_show}
                  onChange={(e) => setEditingPermissions(prev => ({...prev, can_show: e.target.checked}))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="can_show" className="text-sm font-medium text-gray-900 dark:text-white">
                  Visualizar
                </label>
              </div>

              {selectedModuleForPermission && VIEW_VALUES_ELIGIBLE_MODULES.has(selectedModuleForPermission.name) && (
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="can_view_values"
                    checked={editingPermissions.can_view_values}
                    disabled={!editingPermissions.can_show}
                    onChange={(e) => setEditingPermissions(prev => ({...prev, can_view_values: e.target.checked}))}
                    className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="can_view_values" className="text-sm font-medium text-gray-900 dark:text-white">
                    Ver valores
                  </label>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_insert"
                  checked={editingPermissions.can_insert}
                  onChange={(e) => setEditingPermissions(prev => ({...prev, can_insert: e.target.checked}))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="can_insert" className="text-sm font-medium text-gray-900 dark:text-white">
                  Criar
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_update"
                  checked={editingPermissions.can_update}
                  onChange={(e) => setEditingPermissions(prev => ({...prev, can_update: e.target.checked}))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="can_update" className="text-sm font-medium text-gray-900 dark:text-white">
                  Editar
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_delete"
                  checked={editingPermissions.can_delete}
                  onChange={(e) => setEditingPermissions(prev => ({...prev, can_delete: e.target.checked}))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="can_delete" className="text-sm font-medium text-gray-900 dark:text-white">
                  Excluir
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => setIsPermissionModalOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={savePermissionModal}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                Salvar Permissões
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`w-[min(92vw,500px)] max-w-none bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden ${
            toast.type === 'success' ? 'border-l-4 border-green-400' :
            toast.type === 'error' ? 'border-l-4 border-red-400' :
            toast.type === 'warning' ? 'border-l-4 border-yellow-400' :
            'border-l-4 border-blue-400'
          }`}>
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {toast.type === 'success' && <CheckCircleIcon className="h-6 w-6 text-green-400" />}
                  {toast.type === 'error' && <XCircleIcon className="h-6 w-6 text-red-400" />}
                  {toast.type === 'warning' && <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />}
                  {toast.type === 'info' && <UserIcon className="h-6 w-6 text-blue-400" />}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5 overflow-x-auto">
                  <p className={`text-sm font-medium break-words whitespace-normal ${
                    toast.type === 'success' ? 'text-green-800 dark:text-green-200' :
                    toast.type === 'error' ? 'text-red-800 dark:text-red-200' :
                    toast.type === 'warning' ? 'text-yellow-800 dark:text-yellow-200' :
                    'text-blue-800 dark:text-blue-200'
                  }`}>
                    {toast.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    className="bg-white dark:bg-gray-800 rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => setToast(null)}
                  >
                    <span className="sr-only">Fechar</span>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Usuarios
